import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { toast } from 'sonner';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Single source of truth for the user's credit balance. Components
  // that read it (Header, Shop, BetDialog, …) consume the value from
  // here and call refreshCredits() after any action that changes it.
  const [credits, setCredits] = useState(0);
  // Notifications: same single-source-of-truth pattern. The header
  // bell, the dropdown, and the profile page all read from here so
  // the unread badge and the visible list never disagree.
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  // Equipped cosmetics aus dem Virtual Shop — keyed by `effect`.
  // Header rendert davon Avatar-Emoji, Name-Color, Frame, PRO-Badge,
  // Custom-Title. Shop ruft refreshCosmetics() nach jedem
  // setVariant/toggle/purchase, damit der Header live reagiert.
  const [cosmetics, setCosmetics] = useState({});

  const refreshCredits = useCallback(async (userId) => {
    if (!userId) return;
    try {
      const res = await api.credits.getBalance(userId);
      // Backend returns Decimal as string; coerce to Number for display.
      const next = Number(res.data?.balance ?? 0);
      setCredits(Number.isFinite(next) ? next : 0);
    } catch {
      // Leave previous value rather than flicker to 0 on transient errors.
    }
  }, []);

  const refreshCosmetics = useCallback(async () => {
    try {
      const res = await api.shop.getMyItems();
      const map = {};
      (res.data || []).forEach((it) => {
        if (it.is_active && it.effect) map[it.effect] = it;
      });
      setCosmetics(map);
    } catch { /* keep previous */ }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const [allRes, countRes] = await Promise.all([
        api.notifications.getAll(),
        api.notifications.getUnreadCount(),
      ]);
      setNotifications(Array.isArray(allRes.data) ? allRes.data : []);
      setNotificationCount(Number(countRes.data?.count ?? 0) || 0);
    } catch {
      // Leave previous values; transient API hiccups shouldn't blank the bell.
    }
  }, []);

  const markNotificationAsRead = useCallback(async (id) => {
    try {
      await api.notifications.markRead(id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setNotificationCount((c) => Math.max(0, c - 1));
    } catch {
      /* ignore */
    }
  }, []);

  const markAllNotificationsAsRead = useCallback(async () => {
    try {
      await api.notifications.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setNotificationCount(0);
    } catch {
      /* ignore */
    }
  }, []);

  // On mount: try to restore session from httpOnly cookie via /auth/me
  useEffect(() => {
    let cancelled = false;
    const restore = async () => {
      try {
        const res = await api.auth.me();
        if (cancelled) return;
        setUser(res.data);
        if (res.data?.id) {
          await refreshCredits(res.data.id);
          await fetchNotifications();
          await refreshCosmetics();
        }
      } catch {
        // No valid cookie – stay logged out
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    restore();

    // Listen for forced logout (401 interceptor)
    const handleLogout = () => {
      setUser(null);
      setCredits(0);
      setNotifications([]);
      setNotificationCount(0);
      setCosmetics({});
    };
    window.addEventListener('auth:logout', handleLogout);

    // Multi-Tab/Multi-Device-Sync: WebSocketContext leitet wallet_changed
    // -Pushes als window-CustomEvent durch (siehe WebSocketContext.js).
    // Wir uebernehmen den neuen Saldo direkt, ohne extra HTTP-Roundtrip.
    const handleWalletChanged = (evt) => {
      const next = Number(evt?.detail?.balance);
      if (Number.isFinite(next)) setCredits(next);
    };
    window.addEventListener('wallet:changed', handleWalletChanged);

    return () => {
      cancelled = true;
      window.removeEventListener('auth:logout', handleLogout);
      window.removeEventListener('wallet:changed', handleWalletChanged);
    };
  }, [refreshCredits, fetchNotifications, refreshCosmetics]);

  // Poll the unread count once a minute as a baseline. WebSocket
  // pushes can update faster but this guarantees the badge stays
  // honest even when the socket is down.
  useEffect(() => {
    if (!user?.id) return undefined;
    const id = setInterval(() => {
      api.notifications.getUnreadCount()
        .then((res) => setNotificationCount(Number(res.data?.count ?? 0) || 0))
        .catch(() => {});
    }, 60000);
    return () => clearInterval(id);
  }, [user?.id]);

  const login = useCallback(async (email, password) => {
    try {
      const res = await api.auth.login({ email, password });
      // Cookie is set by server; response has user data
      setUser(res.data.user);
      if (res.data.user?.id) {
        await refreshCredits(res.data.user.id);
        await fetchNotifications();
        await refreshCosmetics();
      }
      return res.data;
    } catch (err) {
      // 401 = falsche Credentials, 403 = deaktiviert, sonst generisch.
      // AuthPages.handleSubmit erwartet falsy -> bleibt auf der
      // Login-Seite ohne navigate(), und der Toast informiert den User.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg =
        status === 401 ? 'Falsche E-Mail oder Passwort.' :
        status === 403 ? (detail || 'Konto deaktiviert.') :
        'Login fehlgeschlagen, bitte erneut versuchen.';
      try { toast.error(msg); } catch { /* toast may not be available */ }
      return false;
    }
  }, [refreshCredits, fetchNotifications, refreshCosmetics]);

  const register = useCallback(async (handle, email, password, referralCode, leaderboardVisible = false, turnstileToken = null) => {
    const payload = { handle, email, password, leaderboard_visible: leaderboardVisible };
    // Backend erwartet `referral_code` (NICHT referred_by). Wird im Body
    // mitgeschickt; das Backend ordnet den Werbenden zu.
    if (referralCode) payload.referral_code = referralCode;
    // Turnstile-Captcha-Token (nur wenn aktiviert).
    if (turnstileToken) payload.turnstile_token = turnstileToken;
    const res = await api.auth.register(payload);
    setUser(res.data.user);
    if (res.data.user?.id) {
      await refreshCredits(res.data.user.id);
      await fetchNotifications();
      await refreshCosmetics();
    }
    return res.data;
  }, [refreshCredits, fetchNotifications, refreshCosmetics]);

  const logout = useCallback(async () => {
    try { await api.auth.logout(); } catch { /* ignore */ }
    setUser(null);
    setCredits(0);
    setNotifications([]);
    setNotificationCount(0);
    setCosmetics({});
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.auth.me();
      setUser(res.data);
      if (res.data?.id) {
        await refreshCredits(res.data.id);
        await fetchNotifications();
      }
    } catch {
      setUser(null);
      setCredits(0);
      setNotifications([]);
      setNotificationCount(0);
    }
  }, [refreshCredits, fetchNotifications, refreshCosmetics]);

  // Convenience wrapper components can call without knowing the user id.
  const refreshMyCredits = useCallback(() => {
    if (user?.id) return refreshCredits(user.id);
    return Promise.resolve();
  }, [user?.id, refreshCredits]);

  // Role helpers — admins are operators, not players. The backend
  // rejects bets/credit-purchases/civic-votes/shop-redemptions from
  // admin accounts (require_player dependency); the UI mirrors that
  // by hiding/disabling the corresponding affordances. `isPlayer`
  // covers anyone who isn't an admin (regular users + creators).
  const isAdmin = !!user?.roles?.includes('admin');
  const isPlayer = !!user && !isAdmin;

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      credits,
      notifications,
      notificationCount,
      isAdmin,
      isPlayer,
      login,
      register,
      logout,
      refreshUser,
      refreshCredits: refreshMyCredits,
      fetchNotifications,
      markNotificationAsRead,
      markAllNotificationsAsRead,
      cosmetics,
      refreshCosmetics,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
